import type { PremiumGroupInvoiceLine } from '../types'
import type { ColumnsType } from 'antd/es/table'

import { Table } from 'antd'
import cx from 'classnames'
import { useMemo } from 'react'

import { currency, formatDate } from '../formatters'
import { baseCellClassName, baseHeaderCellClassName } from '../groups'

import { EmptyCellValue } from './empty-cell-value'

const invoiceLinesSortPredicate = (
  a: PremiumGroupInvoiceLine,
  b: PremiumGroupInvoiceLine
): number => {
  const an = Number.parseInt(a.number, 10)
  const bn = Number.parseInt(b.number, 10)

  if (!Number.isNaN(an) && !Number.isNaN(bn)) {
    return an - bn
  }

  return 0
}

export interface InvoiceExpandableRowProps {
  date: string
  lines: PremiumGroupInvoiceLine[]
}

export function InvoiceLines(props: InvoiceExpandableRowProps): JSX.Element {
  const { date, lines } = props
  const columns = useMemo<ColumnsType<PremiumGroupInvoiceLine>>(
    () => [
      {
        title: 'Billing Date',
        align: 'center',
        render: () => formatDate(date),
        onCell: () => ({ className: cx(baseCellClassName) }),
        onHeaderCell: () => ({ className: cx(baseHeaderCellClassName) })
      },
      {
        title: 'Line #',
        dataIndex: 'number',
        align: 'center',
        onCell: () => ({ className: cx(baseCellClassName) }),
        onHeaderCell: () => ({ className: cx(baseHeaderCellClassName) })
      },
      {
        title: 'Description',
        dataIndex: 'description',
        align: 'left',
        render: value => (value as unknown) ?? <EmptyCellValue />,
        onCell: () => ({ className: cx(baseCellClassName) }),
        onHeaderCell: () => ({ className: cx(baseHeaderCellClassName) })
      },
      {
        title: 'Coverage Category',
        dataIndex: 'coverage_category',
        align: 'center',
        render: value => (value as unknown) ?? <EmptyCellValue />,
        onCell: () => ({ className: cx(baseCellClassName) }),
        onHeaderCell: () => ({ className: cx(baseHeaderCellClassName) })
      },
      {
        title: 'Billing Type',
        dataIndex: 'billing_type',
        align: 'center',
        render: value => (value as unknown) ?? <EmptyCellValue />,
        onCell: () => ({ className: cx(baseCellClassName) }),
        onHeaderCell: () => ({ className: cx(baseHeaderCellClassName) })
      },
      {
        title: 'Amount',
        dataIndex: 'amount',
        align: 'right',
        render: value => (typeof value === 'number' ? currency(value) : <EmptyCellValue />),
        onCell: () => ({ className: cx(baseCellClassName) }),
        onHeaderCell: () => ({ className: cx(baseHeaderCellClassName) })
      },
      {
        title: 'Total Credits',
        dataIndex: 'total_credits',
        align: 'right',
        render: value => (typeof value === 'number' ? currency(value) : <EmptyCellValue />),
        onCell: () => ({ className: cx(baseCellClassName) }),
        onHeaderCell: () => ({ className: cx(baseHeaderCellClassName) })
      },
      {
        title: 'Balance Due',
        dataIndex: 'balance_due',
        align: 'right',
        render: value => (typeof value === 'number' ? currency(value) : <EmptyCellValue />),
        onCell: () => ({ className: cx(baseCellClassName) }),
        onHeaderCell: () => ({ className: cx(baseHeaderCellClassName) })
      }
    ],
    [date]
  )

  const dataSource = useMemo(() => [...lines].sort(invoiceLinesSortPredicate), [lines])

  return (
    <Table
      size='small'
      rowKey='id'
      className='rounded-none payment-files-table [&_.ant-table]:![margin-inline:_0]'
      columns={columns}
      dataSource={dataSource}
      pagination={false}
    />
  )
}
